import { useState } from 'react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import MealTimelineCard from './MealTimelineCard'

/**
 * RecentDayCard Component
 *
 * Displays a summary of a past day's nutrition with expandable meal details
 */
export default function RecentDayCard({
  date,
  totals = {
    calories: 0,
    protein: 0,
    carbs: 0,
    fat: 0
  },
  mealCount = 0,
  meals = [],
  goals = {
    calories: 2000,
    protein: 50,
    carbs: 250,
    fat: 75
  },
  isExpanded = false,
  onToggle = () => {},
  onDeleteMeal = () => {},
  onMealUpdated = () => {},
  // VH-161: pass-through so a past day's meal can be copied onto another day.
  onCopyMeal = null
}) {
  const [expandedMeals, setExpandedMeals] = useState({})

  // Calculate goal percentage
  const goalPercent = goals.calories > 0 ? Math.round((totals.calories / goals.calories) * 100) : 0

  // Get color based on goal adherence
  const getGoalColor = (percent) => {
    if (percent >= 80 && percent <= 120) return 'text-green-600 bg-green-50'
    if ((percent >= 60 && percent < 80) || (percent > 120 && percent <= 150)) return 'text-amber-600 bg-amber-50'
    return 'text-red-600 bg-red-50'
  }

  const getProgressBarColor = (percent) => {
    if (percent >= 80 && percent <= 120) return 'bg-green-500'
    if ((percent >= 60 && percent < 80) || (percent > 120 && percent <= 150)) return 'bg-amber-500'
    return 'bg-red-500'
  }

  // Format date
  const formatDate = (dateStr) => {
    const d = new Date(dateStr)
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(d)
  }

  const toggleMeal = (mealId) => {
    setExpandedMeals(prev => ({
      ...prev,
      [mealId]: !prev[mealId]
    }))
  }

  return (
    <div className="bg-surface-container rounded-lg shadow-md border border-gray-200 overflow-hidden">
      {/* Header - Collapsible */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex-grow">
            <h3 className="text-lg font-semibold text-gray-900">{formatDate(date)}</h3>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
              <span className="font-medium">{Math.round(totals.calories)} calories</span>
              <span>•</span>
              <span>{mealCount} {mealCount === 1 ? 'meal' : 'meals'}</span>
            </div>
          </div>

          {/* Goal Badge */}
          <div className="flex items-center space-x-3">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${getGoalColor(goalPercent)}`}>
              {goalPercent}% of goal
            </div>
            {isExpanded ? (
              <ChevronUpIcon className="h-6 w-6 text-gray-400" />
            ) : (
              <ChevronDownIcon className="h-6 w-6 text-gray-400" />
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-3">
          <div
            className={`h-full ${getProgressBarColor(goalPercent)} transition-all duration-300`}
            style={{ width: `${Math.min(goalPercent, 100)}%` }}
          />
        </div>

        {/* Macros Summary */}
        <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
          <div>
            <span className="text-gray-600">P:</span>
            <span className="font-medium text-gray-900 ml-1">{totals.protein.toFixed(1)}g</span>
          </div>
          <div>
            <span className="text-gray-600">C:</span>
            <span className="font-medium text-gray-900 ml-1">{totals.carbs.toFixed(1)}g</span>
          </div>
          <div>
            <span className="text-gray-600">F:</span>
            <span className="font-medium text-gray-900 ml-1">{totals.fat.toFixed(1)}g</span>
          </div>
        </div>
      </div>

      {/* Expanded Content - Meals grouped by type */}
      {isExpanded && meals.length > 0 && (
        <div className="p-4 pt-0 space-y-3 border-t border-gray-200 bg-gray-50">
          <div className="text-sm font-medium text-gray-700 mb-2">Meals:</div>
          {(() => {
            const typeOrder = { breakfast: 0, lunch: 1, dinner: 2, snack: 3 }
            const sorted = [...meals].sort((a, b) => {
              const aOrder = typeOrder[(a.meal_type || '').toLowerCase()] ?? 4
              const bOrder = typeOrder[(b.meal_type || '').toLowerCase()] ?? 4
              if (aOrder !== bOrder) return aOrder - bOrder
              return new Date(a.meal_time) - new Date(b.meal_time)
            })
            return sorted.map((meal) => (
              <MealTimelineCard
                key={meal.id}
                mealId={meal.id}
                mealType={meal.meal_type}
                mealTime={meal.meal_time}
                logDate={meal.log_date}
                photo={meal.photo_url}
                note={meal.description}
                foods={meal.meal_foods || []}
                totals={{
                  calories: meal.total_calories || 0,
                  protein: meal.total_protein_g || 0,
                  carbs: meal.total_carbohydrates_g || 0,
                  fat: meal.total_fat_g || 0
                }}
                isExpanded={expandedMeals[meal.id] || false}
                onToggle={() => toggleMeal(meal.id)}
                onEdit={() => {}}
                onDelete={() => onDeleteMeal(meal.id)}
                onCopyMeal={onCopyMeal}
                onAddPhoto={() => {}}
                onMealUpdated={onMealUpdated}
              />
            ))
          })()}
        </div>
      )}

      {isExpanded && meals.length === 0 && (
        <div className="p-4 pt-0 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-500 italic text-center">No meals logged for this day</p>
        </div>
      )}
    </div>
  )
}
